import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { countAtom, agentState } from "../App";
import { useRecoilState } from "recoil";
import airportList from "../directories/AirportCode";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import { collection, getDocs, addDoc, deleteDoc, where, query, orderBy, limit } from "firebase/firestore";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { enqueueSnackbar } from "notistack";
import { db } from "../firebaseConfig.js";
import Select from "react-select";

// ─── Constants ────────────────────────────────────────────────────────────────

const TITLES         = ["MR", "MISS", "MSTR", "MRS", "MS"];
const BAGGAGE_OPTIONS = ["Hand Carry Only", "1PC", "2PC", "3PC", "23KG", "25KG", "30KG", "35KG", "40KG", "46KG", "50KG", "20KG"];
const ROOM_TYPES     = ["Single Room", "Double Room", "Triple Room", "Quad Room", "Quint Room", "Family Suite Room", "Ocean Deluxe Room", "Ocean Queen Room", "Deluxe Skyline View, 1 King Bed"];
const BOARD_TYPES    = ["Room Only", "Breakfast", "Half Board", "Full Board", "Sahur Included", "Aftar Included", "Sahur/Aftar Included", "All Inclusive"];
const CAR_TYPES      = ["Saloon Car", "GMC", "Hi-Ace", "Coaster", "King Bus", "H1-Van"];
const VISA_TYPES     = ["E-Wavier Visa","ETA","Saudi Tourist Visa", "Umrah Visa", "Visit Visa", "Business Visa", "Family Visa"];
const BANKS          = ["Tide", "Natwest", "Monzo", "Allied", "Starlink", "American"];

const DEFAULT_ROW = {
  users:     { title: "", firstName: "", middleName: "", lastName: "" },
  travels:   { date: "", flightNumber: "", from: "", to: "", departure: "", arrival: "", baggage: "" },
  hotels:    { name: "", date: "", checkOutDate: "", numberOfRooms: "", boardType: "", roomType: "" },
  transport: { date: "", carType: "", pickupLocation: "", dropoffLocation: "" },
  visa:      { visaType: "", numberOfVisa: "" },
};

const SECTIONS = [
  { key: "users",     label: "Passengers",  icon: "👤", color: "#6366f1" },
  { key: "travels",   label: "Flights",     icon: "✈",  color: "#0891b2" },
  { key: "hotels",    label: "Hotels",      icon: "⌂",  color: "#d97706" },
  { key: "transport", label: "Transport",   icon: "⊡",  color: "#7c3aed" },
  { key: "visa",      label: "Visa",        icon: "⊛",  color: "#059669" },
];

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');
`;

// ─── Reusable primitives ──────────────────────────────────────────────────────

const NativeSelect = ({ value, onChange, options, placeholder, name }) => (
  <select name={name} value={value} onChange={onChange} className="field-input field-select">
    <option value="" disabled>{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

// ─── Segment Tables ───────────────────────────────────────────────────────────

const PassengerTable = ({ users, onChange }) => (
  <div className="table-wrap">
    <table>
      <thead><tr><th>Title</th><th>First Name</th><th>Middle Name</th><th>Last Name</th></tr></thead>
      <tbody>
        {users.map((u, i) => (
          <tr key={i}>
            <td><NativeSelect name="title" value={u.title} onChange={(e) => onChange(e, i, "users")} options={TITLES} placeholder="Title" /></td>
            <td><input className="field-input" name="firstName"  value={u.firstName}  onChange={(e) => onChange(e, i, "users")} placeholder="First Name"  /></td>
            <td><input className="field-input" name="middleName" value={u.middleName} onChange={(e) => onChange(e, i, "users")} placeholder="Middle Name" /></td>
            <td><input className="field-input" name="lastName"   value={u.lastName}   onChange={(e) => onChange(e, i, "users")} placeholder="Last Name"   /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const FlightTable = ({ travels, onChange, airportOptions }) => (
  <div className="table-wrap">
    <table>
      <thead><tr><th>Date</th><th>Flight No</th><th>From</th><th>To</th><th>Dep</th><th>Arr</th><th>Baggage</th></tr></thead>
      <tbody>
        {travels.map((t, i) => (
          <tr key={i}>
            <td>
              <input type="date" className="field-input" value={t.date || ""}
                onChange={(e) => onChange({ target: { name: "date", value: e.target.value } }, i, "travels")} />
            </td>
            <td>
              <input className="field-input" style={{ width: 90 }} value={t.flightNumber}
                onChange={(e) => onChange({ target: { name: "flightNumber", value: e.target.value.replace(/\s+/g,"").toUpperCase() } }, i, "travels")}
                placeholder="EK501" />
            </td>
            <td>
              <Select
                value={airportOptions.find((o) => o.value === t.from) || null}
                onChange={(s) => onChange({ target: { name: "from", value: s.value } }, i, "travels")}
                options={airportOptions} placeholder="From" isSearchable
                filterOption={(o, inp) => o.value.toLowerCase().includes(inp.toLowerCase())}
                classNamePrefix="rs" className="rs-wrap"
              />
            </td>
            <td>
              <Select
                value={airportOptions.find((o) => o.value === t.to) || null}
                onChange={(s) => onChange({ target: { name: "to", value: s.value } }, i, "travels")}
                options={airportOptions} placeholder="To" isSearchable
                filterOption={(o, inp) => o.value.toLowerCase().includes(inp.toLowerCase())}
                classNamePrefix="rs" className="rs-wrap"
              />
            </td>
            <td><input className="field-input mono" style={{ width: 80 }} name="departure" value={t.departure} onChange={(e) => onChange(e, i, "travels")} placeholder="0600" /></td>
            <td><input className="field-input mono" style={{ width: 80 }} name="arrival"   value={t.arrival}   onChange={(e) => onChange(e, i, "travels")} placeholder="0900" /></td>
            <td><NativeSelect name="baggage" value={t.baggage} onChange={(e) => onChange(e, i, "travels")} options={BAGGAGE_OPTIONS} placeholder="Baggage" /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const HotelTable = ({ hotels, onChange }) => (
  <div className="table-wrap">
    <table>
      <thead><tr><th>Hotel Name</th><th>Check-in</th><th>Check-out</th><th>Rooms</th><th>Room Type</th><th>Board Type</th></tr></thead>
      <tbody>
        {hotels.map((h, i) => (
          <tr key={i}>
            <td><input className="field-input" name="name"          value={h.name}          onChange={(e) => onChange(e, i, "hotels")} placeholder="Hotel name" /></td>
            <td><input type="date" className="field-input" name="date"          value={h.date}          onChange={(e) => onChange(e, i, "hotels")} /></td>
            <td><input type="date" className="field-input" name="checkOutDate"  value={h.checkOutDate}  onChange={(e) => onChange(e, i, "hotels")} /></td>
            <td><input type="number" className="field-input" style={{ width: 70 }} name="numberOfRooms" value={h.numberOfRooms} onChange={(e) => onChange(e, i, "hotels")} placeholder="1" /></td>
            <td><NativeSelect name="roomType"  value={h.roomType}  onChange={(e) => onChange(e, i, "hotels")} options={ROOM_TYPES}  placeholder="Room Type"  /></td>
            <td><NativeSelect name="boardType" value={h.boardType} onChange={(e) => onChange(e, i, "hotels")} options={BOARD_TYPES} placeholder="Board Type" /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TransportTable = ({ transport, onChange }) => (
  <div className="table-wrap">
    <table>
      <thead><tr><th>Date</th><th>Car Type</th><th>Pickup</th><th>Dropoff</th></tr></thead>
      <tbody>
        {transport.map((t, i) => (
          <tr key={i}>
            <td><input type="date" className="field-input" name="date"            value={t.date}            onChange={(e) => onChange(e, i, "transport")} /></td>
            <td><NativeSelect name="carType" value={t.carType} onChange={(e) => onChange(e, i, "transport")} options={CAR_TYPES} placeholder="Car Type" /></td>
            <td><input className="field-input" name="pickupLocation"  value={t.pickupLocation}  onChange={(e) => onChange(e, i, "transport")} placeholder="Pickup location"  /></td>
            <td><input className="field-input" name="dropoffLocation" value={t.dropoffLocation} onChange={(e) => onChange(e, i, "transport")} placeholder="Dropoff location" /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const VisaTable = ({ visa, onChange }) => (
  <div className="table-wrap">
    <table>
      <thead><tr><th>Visa Type</th><th>Number of Visas</th></tr></thead>
      <tbody>
        {visa.map((v, i) => (
          <tr key={i}>
            <td><NativeSelect name="visaType" value={v.visaType} onChange={(e) => onChange(e, i, "visa")} options={VISA_TYPES} placeholder="Visa Type" /></td>
            <td><input type="number" className="field-input" style={{ width: 120 }} name="numberOfVisa" value={v.numberOfVisa} onChange={(e) => onChange(e, i, "visa")} placeholder="1" /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── Section Card Component ───────────────────────────────────────────────────

const SegmentSection = ({ sectionKey, label, icon, color, count, children, onAdd, onRemove, addLabel, removeLabel }) => {
  const [open, setOpen] = useState(true);
  return (
    <Card $accent={color}>
      <CardHeader onClick={() => setOpen((p) => !p)}>
        <HeaderLeft>
          <IconBadge $color={color}>{icon}</IconBadge>
          <div>
            <CardTitle>{label}</CardTitle>
            <CardSub>{count} {count === 1 ? "row" : "rows"}</CardSub>
          </div>
        </HeaderLeft>
        <HeaderRight>
          <CountPill $color={color}>{count}</CountPill>
          <Chevron $open={open}>▾</Chevron>
        </HeaderRight>
      </CardHeader>

      {open && (
        <CardBody>
          {children}
          <RowActions>
            <AddRowBtn $color={color} type="button" onClick={(e) => { e.stopPropagation(); onAdd(); }}>
              + {addLabel}
            </AddRowBtn>
            {count > 0 && (
              <RemoveRowBtn type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
                − {removeLabel}
              </RemoveRowBtn>
            )}
          </RowActions>
        </CardBody>
      )}
    </Card>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

function Main() {
  const [state, setState] = useRecoilState(countAtom);
  const [agents, setAgents] = useRecoilState(agentState);
  const navigate = useNavigate();

  const { users, travels, hotels, transport, visa, ourReference, agentsName, totalQuote, totalPaid, phoneNumber, emailAddress, bank } = state;
  const airportOptions = airportList.map((a) => ({ value: a.code, label: `${a.name} (${a.code})` }));

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      travels:      prev.travels.length      ? prev.travels      : [],
      users:        prev.users.length        ? prev.users        : [],
      hotels:       prev.hotels.length       ? prev.hotels       : [],
      transport:    prev.transport.length    ? prev.transport    : [],
      visa:         prev.visa.length         ? prev.visa         : [],
      ourReference: prev.ourReference || 0,
      agentsName:   prev.agentsName   || "",
      totalQuote:   prev.totalQuote   || 0,
      totalPaid:    prev.totalPaid    || 0,
      emailAddress: prev.emailAddress || "",
      phoneNumber:  prev.phoneNumber  || "",
      bank:         prev.bank         || "",
    }));
  }, [setState]);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const snap = await getDocs(collection(db, "Agents"));
        setAgents(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (err) { console.error(err); }
    };
    fetchAgents();
  }, []);

  useEffect(() => {
    if (state.users[0]?.firstName         === "") handleRemoveRow("users");
    if (state.travels[0]?.departure       === "") handleRemoveRow("travels");
    if (state.hotels[0]?.name             === "") handleRemoveRow("hotels");
    if (state.visa[0]?.numberOfVisas      === "") handleRemoveRow("visa");
    if (state.transport[0]?.pickupLocation === "") handleRemoveRow("transport");
  }, []);

  const updateState     = (key, value) => setState((prev) => ({ ...prev, [key]: value }));
  const handleAddRow    = (cat) => updateState(cat, [...state[cat], DEFAULT_ROW[cat]]);
  const handleRemoveRow = (cat) => updateState(cat, state[cat].slice(0, -1));

  const handleChange = (e, index, category) => {
    const { name, value } = e.target;
    const updated = [...state[category]];
    updated[index] = { ...updated[index], [name]: value };
    updateState(category, updated);
  };

  const generateFolder = async () => {
    if (!agentsName || !totalQuote || !bank || !phoneNumber) {
      enqueueSnackbar("Fill in Agent, Quote, Bank & Phone Number", { variant: "warning" });
      return;
    }
    try {
      const foldersRef  = collection(db, "FOLDERS");
      const snap        = await getDocs(query(foldersRef, orderBy("FolderNo", "desc"), limit(1)));
      const newFolderNo = snap.empty ? 886 : snap.docs[0].data().FolderNo + 1;
      const formattedDate = new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
      await addDoc(foldersRef, { Agent: agentsName, FolderNo: newFolderNo, TotalPaid: totalPaid, TotalQoute: totalQuote, CreatedAt: formattedDate, bank, emailAddress, phoneNumber });
      updateState("ourReference", newFolderNo);
      enqueueSnackbar(`Folder #${newFolderNo} generated`, { variant: "success" });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error generating folder", { variant: "error" });
    }
  };

  const clearAndUpload = async (collectionName, folderNo, docs) => {
    const ref  = collection(db, collectionName);
    const snap = await getDocs(query(ref, where("FolderNo", "==", folderNo)));
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
    for (const data of docs) await addDoc(ref, { FolderNo: folderNo, ...data });
  };

  const uploadData = async () => {
    try {
      const fmt = (d) => new Date(d).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
      await clearAndUpload("FlightSegments",    ourReference, travels.map((t)  => ({ date: fmt(t.date), flightNumber: t.flightNumber, from: t.from, to: t.to, departure: t.departure, arrival: t.arrival, baggage: t.baggage, notificationSent: false })));
      await clearAndUpload("PassengerSegments", ourReference, users.map((u)    => ({ firstName: u.firstName, lastName: u.lastName, middleName: u.middleName, title: u.title })));
      await clearAndUpload("HotelSegments",     ourReference, hotels.map((h)   => ({ boardType: h.boardType, checkOutDate: h.checkOutDate, date: h.date, name: h.name, numberOfRooms: h.numberOfRooms, roomType: h.roomType })));
      await clearAndUpload("TransportSegments", ourReference, transport.map((t) => ({ carType: t.carType, date: t.date, dropoffLocation: t.dropoffLocation, pickupLocation: t.pickupLocation })));
      await clearAndUpload("VisaSegments",      ourReference, visa.map((v)     => ({ numberOfVisa: v.numberOfVisa, visaType: v.visaType })));
      enqueueSnackbar("All segments saved successfully!", { variant: "success" });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error saving data.", { variant: "error" });
    }
  };

  const handleSubmit = (e) => {
    if (ourReference !== 0 && agentsName && totalQuote !== 0) {
      e.preventDefault();
      navigate("/hero", { state: { users, travels, hotels, transport, visa, ourReference, agentsName, totalQuote, totalPaid, bank } });
    } else {
      enqueueSnackbar("Missing data for invoice!", { variant: "error" });
    }
  };

  const totalPassengers = users.length;
  const totalFlights    = travels.length;

  return (
    <>
      <GlobalStyle />
      <Page>

        {/* ── Page Header ── */}
        <PageHeader>
          <HeaderMeta>
            <PageLabel>Folder Management</PageLabel>
            <PageTitle>New Folder</PageTitle>
            <PageSub>Create a new travel folder with passengers, flights, hotels, transport & visa segments</PageSub>
          </HeaderMeta>

          {ourReference !== 0 && (
            <FolderBadge>
              <span className="lbl">Folder</span>
              <span className="num">#{ourReference}</span>
            </FolderBadge>
          )}
        </PageHeader>

        {/* ── Payment & Reference ── */}
        <Card $accent="#2563eb">
          <CardHeader as="div" style={{ cursor: "default" }}>
            <HeaderLeft>
              <IconBadge $color="#2563eb">⊞</IconBadge>
              <div>
                <CardTitle>Payment & Reference</CardTitle>
                <CardSub>Folder details and payment information</CardSub>
              </div>
            </HeaderLeft>
          </CardHeader>

          <CardBody>
            <FieldGrid cols={3}>
              <FormField label="Folder Number">
                <input type="text" className="field-input mono" value={ourReference} readOnly placeholder="Auto-generated" />
              </FormField>
              <FormField label="Agent">
                <select className="field-input field-select" value={agentsName} onChange={(e) => updateState("agentsName", e.target.value)}>
                  <option value="" disabled>Select agent</option>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.id}</option>)}
                </select>
              </FormField>
              <FormField label="Bank">
                <NativeSelect value={bank} onChange={(e) => updateState("bank", e.target.value)} options={BANKS} placeholder="Select bank" />
              </FormField>
            </FieldGrid>

            <FieldGrid cols={3}>
              <FormField label="Total Quote (£)">
                <input type="number" className="field-input" value={totalQuote} onChange={(e) => updateState("totalQuote", e.target.value)} placeholder="0.00" />
              </FormField>
              {/* <FormField label="Total Paid (£)">
                <input type="number" className="field-input" value={totalPaid}  onChange={(e) => updateState("totalPaid",  e.target.value)} placeholder="0.00" />
              </FormField> */}
              <FormField label="Email Address">
                <input type="email" className="field-input" value={emailAddress} onChange={(e) => updateState("emailAddress", e.target.value)} placeholder="customer@email.com" />
              </FormField>
            </FieldGrid>

            <FieldGrid cols={2}>
              <FormField label="Phone Number">
                <PhoneInput
                  country="gb"
                  value={phoneNumber}
                  onChange={(val) => updateState("phoneNumber", val)}
                  inputStyle={{ width: "100%", height: "42px", paddingLeft: "50px", borderRadius: "8px", border: "1.5px solid #e2e8f0", fontSize: "14px", fontFamily: "inherit", background: "#f8fafc" }}
                  buttonStyle={{ border: "none", background: "transparent", borderRadius: "8px 0 0 8px" }}
                />
              </FormField>
              <FormField label=" ">
                <GenFolderBtn
                  type="button"
                  onClick={() => { if (window.confirm("Generate a new folder? This cannot be undone.")) generateFolder(); }}
                >
                  <span>⊕</span> Generate Folder
                </GenFolderBtn>
              </FormField>
            </FieldGrid>
          </CardBody>
        </Card>

        {/* ── Segment sections ── */}
        <SegmentSection sectionKey="users" label="Passengers" icon="👤" color="#6366f1" count={users.length}
          onAdd={() => handleAddRow("users")} onRemove={() => handleRemoveRow("users")}
          addLabel="Add Passenger" removeLabel="Remove">
          {users.length > 0
            ? <PassengerTable users={users} onChange={handleChange} />
            : <EmptyRows color="#6366f1">No passengers added yet</EmptyRows>}
        </SegmentSection>

        <SegmentSection sectionKey="travels" label="Flights" icon="✈" color="#0891b2" count={travels.length}
          onAdd={() => handleAddRow("travels")} onRemove={() => handleRemoveRow("travels")}
          addLabel="Add Flight" removeLabel="Remove">
          {travels.length > 0
            ? <FlightTable travels={travels} onChange={handleChange} airportOptions={airportOptions} />
            : <EmptyRows color="#0891b2">No flight segments added yet</EmptyRows>}
        </SegmentSection>

        <SegmentSection sectionKey="hotels" label="Hotels" icon="⌂" color="#d97706" count={hotels.length}
          onAdd={() => handleAddRow("hotels")} onRemove={() => handleRemoveRow("hotels")}
          addLabel="Add Hotel" removeLabel="Remove">
          {hotels.length > 0
            ? <HotelTable hotels={hotels} onChange={handleChange} />
            : <EmptyRows color="#d97706">No hotel segments added yet</EmptyRows>}
        </SegmentSection>

        <SegmentSection sectionKey="transport" label="Transport" icon="⊡" color="#7c3aed" count={transport.length}
          onAdd={() => handleAddRow("transport")} onRemove={() => handleRemoveRow("transport")}
          addLabel="Add Transport" removeLabel="Remove">
          {transport.length > 0
            ? <TransportTable transport={transport} onChange={handleChange} />
            : <EmptyRows color="#7c3aed">No transport segments added yet</EmptyRows>}
        </SegmentSection>

        <SegmentSection sectionKey="visa" label="Visa" icon="⊛" color="#059669" count={visa.length}
          onAdd={() => handleAddRow("visa")} onRemove={() => handleRemoveRow("visa")}
          addLabel="Add Visa" removeLabel="Remove">
          {visa.length > 0
            ? <VisaTable visa={visa} onChange={handleChange} />
            : <EmptyRows color="#059669">No visa entries added yet</EmptyRows>}
        </SegmentSection>

        {/* ── Final actions ── */}
        <FinalActions>
          <SaveBtn onClick={uploadData}>
            <span>⊙</span> Save Folder Data
          </SaveBtn>
          <PreviewBtn onClick={handleSubmit}>
            <span>◎</span> Preview Invoice
          </PreviewBtn>
        </FinalActions>

      </Page>
    </>
  );
}

// ─── FormField wrapper ────────────────────────────────────────────────────────

const FormField = ({ label, children }) => (
  <FieldWrap>
    <FieldLabel>{label}</FieldLabel>
    {children}
  </FieldWrap>
);

// ─── Styled Components ────────────────────────────────────────────────────────

const Page = styled.section`
  padding: 32px 28px 80px;
  max-width: 1500px;
  font-family: 'DM Sans', sans-serif;

  /* ── Field inputs ── */
  .field-input {
    width: 100%;
    box-sizing: border-box;
    padding: 9px 12px;
    font-size: 13.5px;
    font-family: 'DM Sans', sans-serif;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
    color: #0f172a;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;

    &:focus {
      border-color: #2563eb;
      background: white;
      box-shadow: 0 0 0 3px #2563eb18;
    }
    &::placeholder { color: #cbd5e1; font-style: normal; }
    &[readonly] { background: #f1f5f9; color: #64748b; cursor: default; }
  }

  .field-select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    padding-right: 28px;
    cursor: pointer;
  }

  .mono {
    font-family: 'DM Mono', monospace;
    font-size: 13px;
  }

  /* ── Tables ── */
  .table-wrap {
    overflow-x: auto;
    border-radius: 8px;
    border: 1px solid #f1f5f9;
    margin-bottom: 4px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 600px;

    th {
      padding: 9px 12px;
      text-align: left;
      font-size: 10.5px;
      font-weight: 600;
      letter-spacing: 0.8px;
      text-transform: uppercase;
      color: #94a3b8;
      background: #f8fafc;
      border-bottom: 1px solid #e2e8f0;
      white-space: nowrap;
    }

    td {
      padding: 7px 8px;
      border-bottom: 0.5px solid #f1f5f9;
      vertical-align: middle;
    }

    tr:last-child td { border-bottom: none; }
    tr:hover td { background: #f8fafc; }
  }

  /* ── React-Select overrides ── */
  .rs-wrap { min-width: 180px; }
  .rs__control {
    border: 1.5px solid #e2e8f0 !important;
    border-radius: 8px !important;
    background: #f8fafc !important;
    min-height: 38px !important;
    font-size: 13.5px !important;
    box-shadow: none !important;
    cursor: pointer;
    &:hover { border-color: #cbd5e1 !important; }
  }
  .rs__control--is-focused {
    border-color: #2563eb !important;
    background: white !important;
    box-shadow: 0 0 0 3px #2563eb18 !important;
  }
  .rs__placeholder { color: #cbd5e1 !important; }
  .rs__single-value { color: #0f172a !important; font-size: 13.5px !important; }
  .rs__indicator-separator { display: none; }
  .rs__dropdown-indicator { color: #94a3b8 !important; }
  .rs__menu { border-radius: 8px !important; border: 1px solid #e2e8f0 !important; box-shadow: 0 8px 24px rgba(0,0,0,0.1) !important; font-size: 13px !important; }
  .rs__option--is-focused { background: #eff6ff !important; color: #1d4ed8 !important; }
  .rs__option--is-selected { background: #2563eb !important; }
`;

// ─── Page header ─────────────────────────────────────────────────────────────

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 28px;
  animation: ${fadeUp} 0.35s ease both;
`;

const HeaderMeta = styled.div``;

const PageLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 4px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 4px;
  letter-spacing: -0.3px;
`;

const PageSub = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0;
  max-width: 560px;
`;

const FolderBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 22px;
  background: #eff6ff;
  border: 1.5px solid #bfdbfe;
  border-radius: 12px;

  .lbl { font-size: 10px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; color: #60a5fa; }
  .num { font-size: 22px; font-weight: 600; color: #1d4ed8; font-family: 'DM Mono', monospace; }
`;

// ─── Card ─────────────────────────────────────────────────────────────────────

const Card = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  margin-bottom: 16px;
  overflow: hidden;
  position: relative;
  animation: ${fadeUp} 0.3s ease both;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${({ $accent }) => $accent || "#e2e8f0"};
    border-radius: 14px 14px 0 0;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid #f8fafc;

  &:hover { background: #fafafa; }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IconBadge = styled.div`
  width: 36px; height: 36px;
  border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px;
  background: ${({ $color }) => $color}18;
  color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
`;

const CardSub = styled.div`
  font-size: 11.5px;
  color: #94a3b8;
  margin-top: 1px;
`;

const CountPill = styled.div`
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $color }) => $color}18;
  color: ${({ $color }) => $color};
`;

const Chevron = styled.span`
  font-size: 14px;
  color: #94a3b8;
  transition: transform 0.2s ease;
  transform: ${({ $open }) => $open ? "rotate(180deg)" : "rotate(0)"};
  display: inline-block;
`;

const CardBody = styled.div`
  padding: 16px 20px 20px;
`;

// ─── Fields ───────────────────────────────────────────────────────────────────

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ cols }) => `repeat(${cols || 3}, 1fr)`};
  gap: 14px;
  margin-bottom: 14px;

  @media (max-width: 768px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #64748b;
`;

// ─── Row actions ─────────────────────────────────────────────────────────────

const RowActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 14px;
`;

const AddRowBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  font-size: 12.5px;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => $color}12;
  border: 1.5px dashed ${({ $color }) => $color}55;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-style: solid;
    border-color: ${({ $color }) => $color};
    background: ${({ $color }) => $color}1a;
  }
`;

const RemoveRowBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  font-size: 12.5px;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  color: #dc2626;
  background: #fef2f2;
  border: 1.5px solid #fecaca;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover { background: #fee2e2; border-color: #fca5a5; }
`;

// ─── Generate Folder button ───────────────────────────────────────────────────

const GenFolderBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  font-size: 13.5px;
  font-weight: 600;
  font-family: 'DM Sans', sans-serif;
  color: white;
  background: #2563eb;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  height: 42px;

  &:hover { background: #1d4ed8; transform: translateY(-1px); box-shadow: 0 4px 16px #2563eb44; }
  &:active { transform: none; background: #1e40af; }
`;

// ─── Empty rows ───────────────────────────────────────────────────────────────

const EmptyRows = styled.div`
  text-align: center;
  padding: 28px 16px;
  font-size: 13px;
  color: #94a3b8;
  border: 1.5px dashed ${({ color }) => color}33;
  border-radius: 8px;
  background: ${({ color }) => color}06;
`;

// ─── Final actions ────────────────────────────────────────────────────────────

const FinalActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const SaveBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'DM Sans', sans-serif;
  color: white;
  background: #059669;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover { background: #047857; transform: translateY(-1px); box-shadow: 0 6px 20px #05996944; }
  &:active { transform: none; }
`;

const PreviewBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'DM Sans', sans-serif;
  color: white;
  background: #2563eb;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover { background: #1d4ed8; transform: translateY(-1px); box-shadow: 0 6px 20px #2563eb44; }
  &:active { transform: none; }
`;

export default Main;